import { useEffect, type KeyboardEvent, type RefObject } from 'react'

/**
 * What Tab can land on inside a dialog - `FRONTEND.md` §9.441, moved here by §9.477.
 *
 * `[href]` and `[tabindex]` are here because a dialog's contents are arbitrary: the invoice page
 * already passes a form, and a list that grows a link later should not silently fall out of the trap.
 * `:not([tabindex="-1"])` keeps out the nodes something else has deliberately removed.
 */
const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'

/**
 * The three things an `aria-modal` overlay owes the keyboard - `FRONTEND.md` §9.477.
 *
 * **Extracted from `ConfirmDialog` verbatim, not redesigned.** §9.441 put a focus trap on that one
 * component and wrote down the reason: *"Without it Tab walks out of the dialog into the page the
 * overlay covers and `aria-modal` says is not there."* §9.467 and §9.469 then gave six more overlays
 * the same attributes and none of them the trap, so six dialogs made a promise the code did not keep.
 * This is that promise, in one place, for all seven.
 *
 * **It lives in `@agos/shared` because `@agos/ui` depends on `@agos/shared` and not the other way
 * round.** `ConfirmDialog` and `CancelOrderModal` are in `ui`; `EmployeeAuthModal` is in `shared`.
 * There is no third package both can reach.
 *
 * @param open whether the overlay is on screen. Every effect stands down when it is not, so a call
 *     site that renders `null` while closed and one that renders a hidden container behave the same.
 * @param container the element the trap is bounded by. **The caller chooses it, deliberately**: the
 *     modals carry `role="dialog"` on the scrim and want the card inside it, while the two driving
 *     sheets carry the role on their `<section>` because the wrapper also holds the dismiss scrim -
 *     §9.469's own reasoning, *"a role there would put the dismiss control inside the dialog it
 *     dismisses"*. The trap wants whichever boundary the role has.
 * @param close what Escape does - §9.480, and **required on purpose**. §9.474's lesson is that a
 *     capability behind an *optional* parameter is dropped at a call site silently, because the code
 *     still compiles and no test on either side sees it; with three required parameters an overlay
 *     that forgets its close cannot build. Pass **`null`** for *not dismissible right now*: every one
 *     of the seven is mid-write at some point and only the caller knows when - `ConfirmDialog` has
 *     `loading`, three have `isSubmitting`, two have a mutation's `isPending`, and
 *     `TransferPaymentSheet` has no submit at all and so passes its close unconditionally. One shape
 *     rather than six copies of *not while submitting* inside six callbacks.
 * @returns `onKeyDown`, to be spread on the same element the ref is on. A handler rather than a
 *     document listener, because two open dialogs - a confirmation over an editor - would otherwise
 *     both answer one Tab. **§9.480 makes that argument load-bearing for Escape too**: `ConfirmDialog`
 *     closed on Escape through a `document` listener, and it is rendered on 52 screens, so a
 *     confirmation over the goods-receipt dialog answered one Escape twice and threw away the receipt
 *     the confirmation was asking about.
 */
export function useModalFocus(
  open: boolean,
  container: RefObject<HTMLElement | null>,
  close: (() => void) | null,
): { onKeyDown: (event: KeyboardEvent) => void } {
  /**
   * Focus in on open, and back where it came from on close - §9.441.
   *
   * **A text input first, then the cancel control - never the confirm button.** The destructive
   * confirmations are why any of this exists, and one that opened with *Odstrániť* focused would turn
   * a reflexive Enter into a deletion. The safe action takes the focus and the dangerous one is one
   * Tab away. The cancel control is found by `[data-dialog-cancel]` rather than by position, so
   * adding a control above it cannot silently move the opening focus onto something destructive.
   */
  useEffect(() => {
    if (!open) return
    const panel = container.current
    if (panel === null) return
    const returnTo = document.activeElement
    const input = panel.querySelector<HTMLElement>('input, select, textarea')
    const target = input ?? panel.querySelector<HTMLElement>('[data-dialog-cancel]')
    target?.focus()
    return () => {
      // Back to the trigger, if it is still on the page - a dialog that deleted the row its button
      // was in leaves nothing to return to, and focusing a detached node throws nothing but moves
      // focus to `body` silently.
      if (returnTo instanceof HTMLElement && document.contains(returnTo)) returnTo.focus()
    }
    // `container` is a ref, stable for the component's life; re-running on it would be wrong as well
    // as impossible to trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  /**
   * Tab stays inside - §9.441.
   *
   * The stops are read **at the moment of the keypress** rather than captured on open, because a
   * dialog's contents can add a control while it is open: the SEPA removal reason appears once a
   * radio is picked, and a captured list would send Tab to a button that is no longer last.
   */
  const onKeyDown = (event: KeyboardEvent) => {
    /**
     * Escape closes the innermost dialog and only that one - §9.480, `TODO.md` 3e.
     *
     * `stopPropagation` is the half that matters and the reason this is not a document listener. The
     * overlays nest in the DOM: a `ConfirmDialog` opened over `GoodsReceiptDialog` is a descendant of
     * it, so without this the keypress bubbles to the outer dialog's handler and **both** close -
     * discarding the receipt the confirmation was asking about. `preventDefault` is for the browser's
     * own Escape behaviours, a native `<dialog>` and an in-progress IME among them.
     *
     * `close === null` is *not dismissible right now*, which is FB-007's own `!loading` condition
     * moved here rather than reinvented at seven call sites.
     */
    if (event.key === 'Escape') {
      /* **`!open` before anything else, and a case of §9.480's own found this missing.** The two driving
         sheets keep their panel mounted while closed, so without this a keypress on a panel nobody can
         see calls `close` - a state change nothing asked for. The Tab branch is covered by its
         `container.current === null` check only for the call sites that render `null`; this one is not.
         §9.477's ninth mutation is the same guard on the focus effect, and it took three attempts to
         prove there too. */
      if (!open || close === null) return
      event.preventDefault()
      event.stopPropagation()
      close()
      return
    }
    if (event.key !== 'Tab') return
    const panel = container.current
    if (panel === null) return
    const stops = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
      (node) => !node.hasAttribute('disabled'),
    )
    if (stops.length === 0) return
    const first = stops[0]!
    const last = stops[stops.length - 1]!
    const active = document.activeElement
    if (event.shiftKey && (active === first || !panel.contains(active))) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && active === last) {
      event.preventDefault()
      first.focus()
    }
  }

  return { onKeyDown }
}
